/**
 * @fileoverview Persistent WebSocket client for the Playlist DJ Cabin / Admin & Overlay (Tile ID 46).
 * Connects to the real-time communication channel to receive live suggestion events,
 * votes, now playing updates, screen mode changes and metrics.
 * 
 * @module utilities/WS/Admin/PlaylistWS
 */

let playlistAdminWebSocket = null;
let isPlaylistAdminConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 20;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

/**
 * Establishes a persistent WebSocket connection with automatic reconnection.
 * 
 * @function ConnectWebSocketForPlaylistAdmin
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked when a 'playlist' message is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback for connection status ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 * @param {string} [tipo='superadmin'] - Canal/rol de conexión ('superadmin', 'admin', 'overlay:playlist', 'pantalla').
 */
export const ConnectWebSocketForPlaylistAdmin = (
    onMessageReceived = () => {},
    onStatusChange = () => {},
    tipo = 'superadmin'
) => {
    if (playlistAdminWebSocket || isPlaylistAdminConnected) {
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        connectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const baseUrl = process.env.REACT_APP_API_URL_WSS || '';
            const separator = baseUrl.includes('?') ? '&' : '?';
            const webSocketEndpointUrl = `${baseUrl}${separator}tipo=${tipo}`;

            playlistAdminWebSocket = new WebSocket(webSocketEndpointUrl);

            playlistAdminWebSocket.onopen = () => {
                isPlaylistAdminConnected = true;
                connectionAttemptCounter = 0;
                onStatusChange('connected');
            };

            playlistAdminWebSocket.onclose = () => {
                isPlaylistAdminConnected = false;
                playlistAdminWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            playlistAdminWebSocket.onerror = () => {
                onStatusChange('error');
            };

            playlistAdminWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    // Emitir sólo mensajes de tipo 'playlist' o payloads compatibles
                    if (parsedPayload && (parsedPayload.type === 'playlist' || !parsedPayload.type)) {
                        onMessageReceived(parsedPayload);
                    }
                } catch (parseError) {
                    // Ignorar mensajes no JSON
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Playlist WebSocket connection.
 * 
 * @function SendPlaylistWebSocketMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendPlaylistWebSocketMessage = (payloadObject) => {
    try {
        if (playlistAdminWebSocket && playlistAdminWebSocket.readyState === WebSocket.OPEN) {
            playlistAdminWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        return false;
    }
};

/**
 * Closes and cleans up the active Playlist WebSocket connection.
 * 
 * @function DisconnectWebSocketForPlaylistAdmin
 */
export const DisconnectWebSocketForPlaylistAdmin = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (playlistAdminWebSocket) {
            playlistAdminWebSocket.onopen = null;
            playlistAdminWebSocket.onclose = null;
            playlistAdminWebSocket.onerror = null;
            playlistAdminWebSocket.onmessage = null;

            if (playlistAdminWebSocket.readyState === WebSocket.OPEN) {
                playlistAdminWebSocket.close();
            } else if (playlistAdminWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = playlistAdminWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            playlistAdminWebSocket = null;
            isPlaylistAdminConnected = false;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};
